import { Box, Button, ButtonGroup, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

// 맵모드(제외/원함/수량/무리규모/갑충석·화폐·지도) 전용: 각 그룹을 쌍따옴표로 묶어서 출력
function buildQuotedMapRegexString(regexObj, regexOperator, wantModJoin) {
  const segments = [];
  const wantMod = regexObj.wantMod || [];
  const ignoreMod = regexObj.ignoreMod || [];

  if (wantMod.length > 0)
    segments.push(wantMod.join(wantModJoin));
  if (ignoreMod.length > 0)
    segments.push('!' + ignoreMod.join('|'));
  if (regexObj.quantity)
    segments.push(regexObj.quantity);
  if (regexObj.packSize)
    segments.push(regexObj.packSize);
  if (regexObj.itemRarity)
    segments.push(regexObj.itemRarity);
  if (regexObj.monsterRarity)
    segments.push(regexObj.monsterRarity);
  if (regexObj.monsterEfficiency)
    segments.push(regexObj.monsterEfficiency);
  if (regexObj.waystoneChance)
    segments.push(regexObj.waystoneChance);

  if (regexOperator === 'OR') {
    // 갑충석/화폐/지도는 OR 모드에서 하나의 그룹으로 합쳐짐
    const combo = [regexObj.scarab, regexObj.currency, regexObj.map].filter(Boolean).join('|');
    if (combo)
      segments.push(combo);
  } else {
    if (regexObj.scarab) segments.push(regexObj.scarab);
    if (regexObj.currency) segments.push(regexObj.currency);
    if (regexObj.map) segments.push(regexObj.map);
  }

  return segments.map((s) => `"${s}"`).join(' ');
}

// 서판(카테고리/OR목록/AND목록) 전용: 카테고리·OR목록은 |로 묶어 각각 하나의 따옴표로,
// AND목록은 항목마다 개별로 따옴표를 씌워야 함(따옴표 안에 공백이 있으면 POE 검색이
// 그 공백까지 포함한 하나의 문구로 취급해 AND 조건 두 개로 분리되지 않음)
function buildQuotedTabletRegexString(regexObj) {
  const category = regexObj.category || [];
  const orMod = regexObj.orMod || [];
  const andMod = regexObj.andMod || [];

  const segments = [];
  if (category.length > 0)
    segments.push(category.join('|'));
  if (orMod.length > 0)
    segments.push(orMod.join('|'));
  andMod.forEach((mod) => {
    if (mod)
      segments.push(mod);
  });

  return segments.map((s) => `"${s}"`).join(' ');
}

function buildRegexString(regexObj, isKor, regexOperator, wantModJoin = '|', quoteGroups = false, tabletMode = false) {
  if (tabletMode)
    return buildQuotedTabletRegexString(regexObj);
  if (quoteGroups)
    return buildQuotedMapRegexString(regexObj, regexOperator, wantModJoin);

  let totalRegex = '';
  for (const [key, value] of Object.entries(regexObj)) {
    if(value != '') {
      if(key === 'wantMod') {
        totalRegex += value.join(wantModJoin);
        if(!isKor)
          totalRegex = '\"' + totalRegex + '\"'
      } else if(key === 'ignoreMod') {
        const temp = '!' + value.join('|');
        if(!isKor)
          totalRegex = totalRegex + '\"' + temp + '\"'
        else
          totalRegex += temp;
      }
      else
        totalRegex += value;

      if(regexOperator==='OR' && (regexObj.currency !== '' || regexObj.scarab !== '' || (regexObj.map !== undefined && regexObj.map !== '')))
        if(key === 'currency' || key === 'scarab' || key === 'map')
          totalRegex += '|';
        else
          totalRegex += ' ';
      else
        totalRegex += ' ';
    }
  }
  return totalRegex.slice(0, -1);
}

function RegexResult({ result, setResult, setSearchWord, engResult, setEngResult, isKor, setIsKor, setShow17Mod, regexOperator, setRegexOperator, version, isItem, setSelected, onItemResetExtras, itemExtraRegexChunks, wantModJoin = '|', saveType, defaultResult, tabletMode = false, saveResultForLoad = false }) {
  const [reg, setReg] = useState('')
  const [regSize, setRegSize] = useState(0);

  const copyClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(reg);
      alert('복사 완료!');
    } catch (error) {
      alert('복사 실패!');
    }
  };

  function saveLocal() {
    if(!reg) {
      alert('저장할 정규식이 없습니다.');
      return;
    }

    const name = prompt('저장할 이름을 입력하세요');
    if(!name)
      return;

    const baseName = name.trim();
    if(!baseName)
      return;

    const storageKey = `poeregex${ version ? '2' : ''}`;

    if (isItem) {
      const extras = itemExtraRegexChunks || [];
      // 불러오기를 지원하려면 선택 상태(result)도 함께 저장해야 함(청크 분할 저장은 대상 제외)
      const loadFields = (saveResultForLoad && extras.length === 0)
        ? { result: { ...result }, engResult: { ...result } }
        : {};
      let entries;
      if (extras.length > 0) {
        const parts = [reg, ...extras];
        entries = parts.map((regexStr, i) => ({
          name: `${baseName}-${i + 1}`,
          regex: regexStr,
          ...(saveType ? { type: saveType } : {}),
        }));
      } else {
        entries = [{
          name: baseName,
          regex: reg,
          ...(saveType ? { type: saveType } : {}),
          ...loadFields,
        }];
      }

      const localRegex = localStorage.getItem(storageKey) === null
        ? []
        : JSON.parse(localStorage.getItem(storageKey));
      localStorage.setItem(storageKey, JSON.stringify([...localRegex, ...entries]));
      return;
    }

    const regexKor = buildRegexString(result, true, regexOperator, wantModJoin, !isItem, tabletMode);
    const regexEng = buildRegexString(engResult, false, regexOperator, wantModJoin, !isItem, tabletMode);

    // 수량/규모 등 입력창의 원본 숫자를 같이 저장해두면, 나중에 불러올 때 입력칸까지 복원 가능
    const rawFields = {};
    if (defaultResult) {
      Object.keys(defaultResult).forEach((key) => {
        if (key === 'wantMod' || key === 'ignoreMod') return;
        const el = document.getElementById(key);
        if (el && el.value) rawFields[key] = el.value;
      });
    }

    const saveRegex = {
      name: baseName,
      regex: regexKor,
      regexKor,
      regexEng,
      result: { ...result },
      engResult: { ...engResult },
      ...(Object.keys(rawFields).length > 0 ? { rawFields } : {}),
    };

    if(localStorage.getItem(storageKey) === null) {
      localStorage.setItem(storageKey, JSON.stringify([saveRegex]));
    } else {
      let localRegex = JSON.parse(localStorage.getItem(storageKey));
      localRegex = [...localRegex, saveRegex];
      localStorage.setItem(storageKey, JSON.stringify(localRegex));
    }
  }

  useEffect(() => {
    const regexObj = (isKor ? result : engResult) || result;
    setReg(buildRegexString(regexObj, isKor, regexOperator, wantModJoin, !isItem, tabletMode));
  }, [result, engResult, isKor, regexOperator, wantModJoin, isItem, tabletMode])

  useEffect(() => {
     setRegSize(reg.length);
  }, [reg])

  function itemReset() {
    setResult(defaultResult ? { ...defaultResult } : { 'wantMod' : [] })
    setSelected([])
    setSearchWord()
    onItemResetExtras?.()
  }

  function resetHandler() {
    document.querySelector('.modSearch .MuiInputBase-inputTypeSearch').value = '';
    if (isItem) {
      itemReset()
      return;
    }

    const next = defaultResult || { wantMod: [], ignoreMod: [] };
    setResult({ ...next });
    setEngResult({ ...next });

    Object.keys(next).forEach((key) => {
      if (key === 'wantMod' || key === 'ignoreMod') return;
      const el = document.getElementById(key);
      if (el) el.value = '';
    });

    setSearchWord('');
    setIsKor(true);
    setShow17Mod(false);
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'rgba(11,14,17,0.92)',
        backdropFilter: 'blur(6px)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        mb: 2,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            title={reg}
            sx={{
              fontFamily: 'monospace',
              fontSize: { xs: 14, sm: 17 },
              lineHeight: 1.4,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-all',
              minHeight: { xs: 20, sm: 24 },
              color: reg ? 'text.primary' : 'text.secondary',
            }}
          >
            {reg || '옵션을 선택하면 정규식이 여기에 표시됩니다'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: regSize > 250 ? 'error.main' : 'text.secondary', fontWeight: regSize > 250 ? 700 : 400 }}
          >
            {regSize <= 250 ? `길이 : ${regSize} / 250` : '최대 글자 수 초과'}
          </Typography>
        </Box>
        <ButtonGroup
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'stretch', sm: 'center' },
            '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } },
          }}
        >
          <Button variant="outlined" color="success" onClick={copyClipBoard}>복사</Button>
          <Button variant="outlined" onClick={saveLocal}>저장</Button>
          <Button variant="outlined" color="error" onClick={resetHandler}>리셋</Button>
        </ButtonGroup>
      </Stack>
    </Paper>
  );
}

export default RegexResult;
